import React, { Component } from 'react';
import { BlazeTableColDef, BlazeTableOptions } from 'components/blazeTable/BlazeTable.types';
import BlazeTable from 'components/blazeTable/BlazeTable';
import BlazeTableActions from 'components/blazeTable/BlazeTableActions';
import i18n from 'i18n';
import { ApplicationState } from 'store/rootReducer';
import {
  deleteMarFileThunk,
  downloadMarThunk,
  toggleExpandedMarFilesRow,
  updateAssetThunk,
} from 'store/marFiles/marFilesActions';
import { connect } from 'react-redux';
import styles from './MarFilesList.module.css';
import { confirm } from 'components/ui/modal/BlazeConfirmationModal';
import MarFileExpandedDetailItem from 'views/AssetCatalog/MarFiles/MarsTable/MarFileExpandedDetailItem';
import { MarAsset } from 'types/rest/marFilesTypes';
import MarFilesHeader from 'views/AssetCatalog/MarFiles/MarFilesHeader';
import SimpleTagList from 'components/ui/tags/SimpleTagList';
import {
  TransactionCountComponent,
  TransportProtocolComponentCell,
  VirtualServiceComponent,
} from 'utils/mockServiceUtils';
import MarFilesListPopover from './MarFileListPopover';
import BzmIcon from '../../../../components/icons/BzmIcon';
import MarEolAlert from '../../../../components/alerts/MarEolAlert';
import ElevioTooltip from '../../../../components/overlays/helpTooltip/TitleHelpTooltip/ElevioTooltip';

interface MarFilesListProps {
  data: MarAsset[];
  onFilterChange: any;
}

/**
 * MAR Files List.
 */
class MarFilesList extends Component<AllProps> {
  tableRef: any = React.createRef<BlazeTable>();

  state = {
    selectedMarFilesCount: [] as MarAsset[],
  };

  componentDidMount() {
    this.props.onFilterChange(this.unSelectAll);
  }

  getTable = (): BlazeTable => {
    return this.tableRef as BlazeTable;
  };

  updateMarFile = (asset: MarAsset) => {
    this.props.updateAsset(asset);
  };

  unSelectAll = (): any => {
    this.setState({ selectedMarFilesCount: [] });
    this.getTable().deSelectAll();
  };

  deleteElement = (marFile: MarAsset) => {
    const modalBody = (
      <p>
        {i18n.t('marFiles:table.deleteConfirmation')} <b className="text-break">{marFile.name}</b>?
      </p>
    );
    confirm({
      confirmation: modalBody,
      title: i18n.t('marFiles:table.deleteMarFile'),
      yesOptionTitle: i18n.t('common:actions.delete'),
    }).then(() => {
      this.props.deleteMarFile(marFile);
    });
  };
  downloadMar = (marFile: MarAsset) => {
    this.props.downloadMar(marFile);
  };

  CustomHeaderComponent = () => {
    return (
      <div className={styles.serviceHeaderCell} title={i18n.t('marFiles:header.service')}>
        {i18n.t('marFiles:header.service')}
        <ElevioTooltip elevioId={'Service_MarFiles_table'} id="MarFilesServiceColumnHelpId" />
      </div>
    );
  };

  ActionsComponent = (data: MarAsset) => {
    return (
      <BlazeTableActions>
        <button
          disabled={this.props.isPrivateCloudReadOnly}
          title={i18n.t('marFiles:table.downloadMarFileToolTip')}
          id={'downloadMarFile-' + data.id}
          onClick={() => this.downloadMar(data)}
          className="btn btn-link-dark btn-no-hover"
        >
          <BzmIcon variant={'icon-bzm-download'} className={'icon-xs'} />
        </button>
        <button
          disabled={this.props.isPrivateCloudReadOnly}
          title={i18n.t('marFiles:table.deleteMarFileToolTip')}
          id={'deletedMarFile-' + data.id}
          onClick={() => this.deleteElement(data)}
          className="btn btn-link-dark"
        >
          <BzmIcon variant={'icon-bzm-delete'} className={'icon-xs'} />
        </button>
      </BlazeTableActions>
    );
  };

  ExpandDetailComponent = (rowData: MarAsset) => {
    return (
      <div className="m-4">
        <MarFileExpandedDetailItem isReadOnly={true} updateMarFile={this.updateMarFile} marFile={rowData} />
      </div>
    );
  };

  // Definition of table columns
  colsDefinition: BlazeTableColDef[] = [
    {
      header: i18n.t('marFiles:header.id'),
      width: 70,
      accessor: 'id',
    },
    {
      header: i18n.t('marFiles:header.name'),
      isExpandLink: true,
      accessor: 'name',
    },
    {
      header: i18n.t('marFiles:header.transportProtocol'),
      isExpandLink: false,
      cellComponent: TransportProtocolComponentCell,
      accessor: 'transportProtocol',
    },
    {
      header: '',
      headerComponent: this.CustomHeaderComponent(),
      accessor: 'serviceName',
    },
    {
      header: i18n.t('marFiles:header.virtualService'),
      isExpandLink: false,
      cellComponent: VirtualServiceComponent,
      accessor: '',
    },
    {
      header: i18n.t('marFiles:header.transactions'),
      isExpandLink: false,
      cellComponent: TransactionCountComponent,
      accessor: '',
    },
    {
      header: i18n.t('marFiles:header.tags'),
      cellComponent: (rowData: any) => <SimpleTagList tags={rowData.tags} />,
      accessor: '',
    },
    {
      header: 'Virtual Services',
      isExpandLink: false,
      cellComponent: (rowData: any) => <MarFilesListPopover data={rowData} />,
      accessor: '',
    },
    {
      header: i18n.t('marFiles:header.actions'),
      cellComponent: (rowData: any) => this.ActionsComponent(rowData),
      accessor: '',
      width: 100,
    },
  ];

  tableOptions: BlazeTableOptions = {
    expandDetailComponent: (rowData: any) => this.ExpandDetailComponent(rowData),
    showRowExpander: true,
    selectAllDropdown: true,
    columnDef: this.colsDefinition,
    pageable: true,
    selectable: true,
    pageSize: 10,
    minWidth: 950,
    minHeight: 450,
    tableName: 'marFilesList',
    noResultsText: i18n.t('marFiles:table.noResultsText'),
    disabled: this.props.isPrivateCloudReadOnly,
  };

  onSelectionChange = (items: MarAsset[]) => {
    const assetIds = items.map(item => item.id);
    this.setState({ selectedMarFilesCount: assetIds });
  };

  toggleExpandDetail = async (id: number | null) => {
    if (id === this.props.expandedMarFileId) {
      this.props.toggleExpandedMarFilesRow(null); // collapse
    } else {
      this.props.toggleExpandedMarFilesRow(id); //expand
    }
  };

  render() {
    return (
      <>
        <MarFilesHeader
          unSelectAll={this.unSelectAll}
          selectedMarFilesData={this.state.selectedMarFilesCount}
          selectedMarAssetId={this.props.selectedMarAssetId}
          selectedMarFileName={this.props.selectedMarFileName}
        />

        <div className={'mt-2 mb-2'}>
          <MarEolAlert />
        </div>

        <BlazeTable
          isLoading={this.props.isLoading}
          options={this.tableOptions}
          data={this.props.data}
          onRef={(ref: any) => (this.tableRef = ref)}
          onSelectionChange={this.onSelectionChange}
          showPageSizeDropdown={true}
          expandedItem={this.props.expandedMarFileId}
          onExpandedItemChange={this.toggleExpandDetail}
          pageSizeOptions={[5, 10, 25]}
        />
      </>
    );
  }
}

const mapStateToProps = (state: ApplicationState) => {
  return {
    isLoading: state.marFiles.isLoading,
    selectedMarAssetId: state.marFiles.selectedMarAssetId,
    selectedMarFileName: state.marFiles.selectedMarFileName,
    isPrivateCloudReadOnly: state.blazeUserConfig.isPrivateCloudReadOnly,
    expandedMarFileId: state.marFiles.expandedMarFileId,
    selectedTagVal: state.marFiles.selectedTagVal,
  };
};

const mapDispatchToProps = {
  deleteMarFile: deleteMarFileThunk,
  updateAsset: updateAssetThunk,
  downloadMar: downloadMarThunk,
  toggleExpandedMarFilesRow,
};

type MapStateToProps = ReturnType<typeof mapStateToProps>;
type MapDispatchToProps = typeof mapDispatchToProps;
type AllProps = MapStateToProps & MapDispatchToProps & MarFilesListProps;

export default connect(mapStateToProps, mapDispatchToProps)(MarFilesList);
